import React, { useState, useCallback } from "react";
import { HexColorInput, HexColorPicker } from "react-colorful";
import MinColorCard from "./MiniColorCard";
import { darkOrLightColor } from "../../helpers/darkOrLightColor";
import {
  color_picker_scheme_wrapper,
  react_colorful,
  react_colorful_input,
  react_colorful_last_control,
  title_card,
  grid_colors,
  picker_card,
  main_color_preview,
  actions_bar,
  action_btn,
  toast,
} from "../../styles/modules/color_picker_scheme.module.css";

// Textos multiidioma
const TEXTS = {
  es: {
    chooseColor: "Escoge el color principal",
    copyAll: "Copiar todos",
    copyCSS: "Copiar CSS",
    random: "Aleatorio",
    copied: "¡Copiado!",
  },
  en: {
    chooseColor: "Choose the main color",
    copyAll: "Copy all",
    copyCSS: "Copy CSS",
    random: "Random",
    copied: "Copied!",
  },
  pt: {
    chooseColor: "Escolha a cor principal",
    copyAll: "Copiar todos",
    copyCSS: "Copiar CSS",
    random: "Aleatório",
    copied: "Copiado!",
  },
  fr: {
    chooseColor: "Choisissez la couleur principale",
    copyAll: "Copier tout",
    copyCSS: "Copier CSS",
    random: "Aléatoire",
    copied: "Copié!",
  },
  de: {
    chooseColor: "Wähle die Hauptfarbe",
    copyAll: "Alle kopieren",
    copyCSS: "CSS kopieren",
    random: "Zufällig",
    copied: "Kopiert!",
  },
};

function ColorPickerScheme({
  mainColor,
  setMainColor,
  colorScheme,
  schemeName,
  locale,
}) {
  const texts = TEXTS[locale] || TEXTS.es;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Mostrar toast
  const displayToast = useCallback(message => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  // Copiar color individual
  const handleColorCopy = useCallback(
    color => {
      displayToast(`${texts.copied} ${color}`);
    },
    [displayToast, texts.copied]
  );

  // Copiar todos los colores
  const copyAllColors = useCallback(() => {
    const colorsText = colorScheme.join(", ");
    navigator.clipboard.writeText(colorsText);
    displayToast(texts.copied);
  }, [colorScheme, displayToast, texts.copied]);

  // Copiar como CSS
  const copyCSSVariables = useCallback(() => {
    const css = `:root {\n${colorScheme
      .map((c, i) => `  --scheme-color-${i + 1}: ${c};`)
      .join("\n")}\n}`;
    navigator.clipboard.writeText(css);
    displayToast(texts.copied);
  }, [colorScheme, displayToast, texts.copied]);

  // Color aleatorio
  const randomColor = useCallback(() => {
    const random =
      "#" +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0");
    setMainColor(random);
  }, [setMainColor]);

  const mainColorHex = mainColor?.toUpperCase() || "#000000";
  const textColor = darkOrLightColor(mainColor);

  return (
    <div className={color_picker_scheme_wrapper}>
      {/* Panel del color picker */}
      <div className={picker_card}>
        <h3 className={title_card}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="13.5" cy="6.5" r="2.5" />
            <circle cx="17.5" cy="10.5" r="2.5" />
            <circle cx="8.5" cy="7.5" r="2.5" />
            <circle cx="6.5" cy="12.5" r="2.5" />
            <path d="M12 22c-4.97 0-9-2.582-9-7v-.088C3 12.794 4.338 11.1 6.375 10c1.949-1.052 3.101-2.99 2.813-5l-.563-3 2.086.795c.64.244 1.064.858 1.064 1.54v.665c0 1.657 1.343 3 3 3h.225c1.553 0 2.775 1.3 2.775 2.85v.65c0 1.657 1.343 3 3 3H22c-.663 2.393-3.006 4.5-6 4.5h-4z" />
          </svg>
          {texts.chooseColor}
        </h3>
        <div>
          <HexColorPicker
            className={`${react_colorful} ${react_colorful_last_control}`}
            color={mainColor}
            onChange={setMainColor}
          />
          <HexColorInput
            className={react_colorful_input}
            color={mainColor}
            onChange={setMainColor}
          />
        </div>

        {/* Preview del color principal */}
        <div
          className={main_color_preview}
          style={{ backgroundColor: mainColorHex, color: textColor }}
        >
          {mainColorHex}
        </div>

        {/* Botón aleatorio */}
        <div className={actions_bar}>
          <button className={action_btn} onClick={randomColor}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {texts.random}
          </button>
        </div>
      </div>

      {/* Panel del esquema de colores */}
      <div className={picker_card}>
        <h3 className={title_card}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <rect x="7" y="7" width="3" height="3" />
            <rect x="14" y="7" width="3" height="3" />
            <rect x="7" y="14" width="3" height="3" />
            <rect x="14" y="14" width="3" height="3" />
          </svg>
          {schemeName}
        </h3>
        <div className={grid_colors}>
          {colorScheme?.map((color, i) => (
            <MinColorCard key={i} color={color} onCopy={handleColorCopy} />
          ))}
        </div>

        {/* Acciones */}
        <div className={actions_bar}>
          <button className={action_btn} onClick={copyAllColors}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {texts.copyAll}
          </button>
          <button className={action_btn} onClick={copyCSSVariables}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            {texts.copyCSS}
          </button>
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className={toast}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default ColorPickerScheme;
